// Data-driven win-rate highlighting for skills and gear.
// Builds lookups from rosterStats on first use; components call
// wrColor(dim, value) to get a green/red text color (or null for neutral).

import { rosterStats } from './preload.js';
import { bfFit } from './unitGrade.js';

// Built on first use, not at import: rosterStats arrives from S3 after this module
// loads (lib/preload.js), though always before anything can render and call in.
let tabs = null;
function t() {
  if (!tabs) {
    const lookup = new Map();
    for (const [dim, entries] of Object.entries(rosterStats.dims || {})) {
      const m = new Map();
      for (const e of entries) m.set(e.value, e.winRate);
      lookup.set(dim, m);
    }
    tabs = { lookup, cross: rosterStats.crossByClass || {}, cond: rosterStats.conditional || {}, monsterBF: rosterStats.monsterBF || {} };
  }
  return tabs;
}

const DEAD_ZONE = 1.5;

function intensityColor(delta) {
  const abs = Math.abs(delta);
  if (abs < DEAD_ZONE) return null;
  const t = Math.min((abs - DEAD_ZONE) / 8, 1);
  const hue = delta > 0 ? 130 : 0;
  const sat = 40 + t * 50;
  const lit = 65 - t * 15;
  return `hsl(${hue}, ${sat}%, ${lit}%)`;
}

// Look up the class+gender baseline win rate (for class-relative comparisons).
function classBaseline(cls, gender) {
  if (!cls) return 50;
  const cg = gender && gender !== 'Monster' ? `${cls} ${gender}` : cls;
  const m = t().lookup.get('ClassGender');
  if (m) { const wr = m.get(cg); if (wr != null) return wr; }
  const mc = t().lookup.get('Class');
  if (mc) { const wr = mc.get(cls); if (wr != null) return wr; }
  return 50;
}

// Look up win rate: tries gendered cross-tab → plain class cross-tab → flat dims.
function resolveWR(dim, value, cls, gender) {
  const { cross, lookup } = t();
  if (cls) {
    const cv = cross[dim] && cross[dim][value];
    if (cv) {
      if (gender && gender !== 'Monster') { const hit = cv.find((x) => x.value === cls + ' ' + gender); if (hit) return hit.winRate; }
      const hit = cv.find((x) => x.value === cls); if (hit) return hit.winRate;
    }
  }
  const m = lookup.get(dim);
  return m ? m.get(value) ?? null : null;
}

export function wrColor(dim, value, cls, gender) {
  if (!value) return null;
  const wr = resolveWR(dim, value, cls, gender);
  if (wr == null) return null;
  // Abilities are compared to the CLASS baseline, not 50% — otherwise every
  // ability on a good class glows green (a 54% ability on a 53% Wizard is
  // barely above average for that class, not a +4pp standout).
  const base = dim === 'Ability' ? classBaseline(cls, gender) : 50;
  return intensityColor(wr - base);
}

export function wrTitle(dim, value, cls, gender) {
  if (!value) return null;
  const wr = resolveWR(dim, value, cls, gender);
  if (wr == null) return null;
  if (dim === 'Ability') {
    const base = classBaseline(cls, gender);
    const delta = wr - base;
    return `${wr.toFixed(1)}% win rate (${delta >= 0 ? '+' : ''}${delta.toFixed(1)}pp vs ${cls} baseline)`;
  }
  return `${wr.toFixed(1)}% win rate`;
}

// Archetype-conditional stat lookup (StatPA, StatMA, StatSpeed, StatHP,
// Brave, Faith). Uses rosterStats.conditional[dim][arch] which is bucketed.

// Resolve the bucket win rate the SAME way unitGrade.js's condWR does: try the
// class+gender table first (e.g. "Oracle Female"), fall back to the archetype
// pool (e.g. "melee"). The stat-block color MUST use this — comparing an
// archetype-pooled win rate against the class+gender baseline (grade.typeWR)
// flips the sign for classes weaker than their archetype average, rendering a
// below-class stat green (the long-standing "low Brave shows green" bug).
function condStatWR(dim, arch, bucket, clsGender) {
  const c = t().cond[dim];
  if (!c) return null;
  if (clsGender && c[clsGender]) { const hit = c[clsGender].find((x) => x.value === bucket); if (hit) return hit.winRate; }
  const a = c[arch];
  if (!a) return null;
  const hit = a.find((x) => x.value === bucket);
  return hit ? hit.winRate : null;
}

export function wrStatColor(dim, arch, bucket, baseline, clsGender) {
  if (!bucket || !arch) return null;
  const wr = condStatWR(dim, arch, bucket, clsGender);
  if (wr == null) return null;
  return intensityColor(wr - (baseline ?? 50));
}

export function wrStatTitle(dim, arch, bucket, baseline, clsGender) {
  if (!bucket || !arch) return null;
  const wr = condStatWR(dim, arch, bucket, clsGender);
  if (wr == null) return null;
  return `${wr.toFixed(1)}% win rate (${clsGender || arch})`;
}

// MONSTER Brave/Faith color. Monsters must NOT use the conditional-stat path
// above: their only conditional bucket is the ALL-MONSTERS-POOLED archetype key,
// so comparing that pooled win rate against a strong monster's own high baseline
// (grade.typeWR) paints its Brave/Faith red even where its OWN B/F trend is
// positive — and it disagrees with the grade's +/- (which reads the per-monster
// FITTED trend). Color instead by that same fit (unitGrade.bfFit) at the shown
// B/F value, so the stat color and the grade contribution always agree in sign.
function monsterBFWR(cls, dim, value) {
  const mb = cls && t().monsterBF[cls];
  const fit = bfFit(mb && mb[dim === 'Faith' ? 'faith' : 'brave']);
  return fit ? fit.predict(value) : null;
}
export function wrMonsterBFColor(cls, dim, value, typeWR) {
  const wr = monsterBFWR(cls, dim, value);
  return wr == null ? null : intensityColor(wr - (typeWR ?? 50));
}
export function wrMonsterBFTitle(cls, dim, value, typeWR) {
  const wr = monsterBFWR(cls, dim, value);
  if (wr == null) return null;
  const d = wr - (typeWR ?? 50);
  return `${wr.toFixed(1)}% win rate (${d >= 0 ? '+' : ''}${d.toFixed(1)}pp vs ${cls || 'type'})`;
}
